import type { TaxonomyNode } from '@/api/generatedApi';
import { getDepthColor } from '@/pages/TaxonomyPage/TaxonomyTree/taxonomyTreeConstants';
import { Close, FolderOpen, Label } from '@mui/icons-material';
import { Box, Chip } from '@mui/material';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import { memo, useEffect, useRef, useState } from 'react';
import { RemoveConfirmation } from './AssetLabels';
import { PickerNodeLevel } from './PickerNodeLevel';

const PickerTreeItemComponent = ({
  node,
  depth,
  taxonomyId,
  expandedSet,
  currentNodeId,
  ancestorPath,
  scrollContainerRef,
  onSelect,
  onRemove,
  searchMatchIds,
  searchVisibleIds,
}: {
  node: TaxonomyNode;
  depth: number;
  taxonomyId: string;
  expandedSet: Set<string>;
  currentNodeId: string | undefined;
  ancestorPath: string[] | undefined;
  scrollContainerRef: React.RefObject<HTMLElement | null>;
  onSelect: (node: TaxonomyNode, depth: number) => void;
  onRemove: (() => void) | undefined;
  searchMatchIds?: Set<string>;
  searchVisibleIds?: Set<string>;
}) => {
  const isExpanded = expandedSet.has(node.id);
  const isLeaf = !node.hasChildren;
  const isCurrent = node.id === currentNodeId;
  const isSearchMatch = searchMatchIds?.has(node.id);
  const isDimmed = searchMatchIds && !isSearchMatch && isLeaf && !isCurrent;
  const color = getDepthColor(depth);
  const chipTextColor = isLeaf ? 'text.primary' : 'text.secondary';
  const itemReference = useRef<HTMLLIElement>(null);
  const [confirmAnchor, setConfirmAnchor] = useState<HTMLElement | undefined>();

  useEffect(() => {
    if (!isCurrent || !itemReference.current) return;
    const timer = setTimeout(() => {
      itemReference.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
    return () => clearTimeout(timer);
  }, [isCurrent]);

  return (
    <>
      <TreeItem
        ref={itemReference}
        itemId={node.id}
        label={
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              minHeight: 36,
              cursor: isLeaf ? 'pointer' : 'default',
            }}
            onClick={
              isLeaf && !isCurrent
                ? event => {
                    event.stopPropagation();
                    onSelect(node, depth);
                  }
                : undefined
            }
          >
            <Chip
              icon={
                isLeaf ? (
                  <Label sx={{ fontSize: 16, color: `${color} !important` }} />
                ) : (
                  <FolderOpen sx={{ fontSize: 16, color: 'text.disabled' }} />
                )
              }
              label={node.name}
              size='small'
              variant={isCurrent ? 'filled' : 'outlined'}
              {...(isCurrent &&
                onRemove && {
                  onDelete: (event: React.SyntheticEvent) => {
                    event.stopPropagation();
                    setConfirmAnchor(event.currentTarget as HTMLElement);
                  },
                  deleteIcon: <Close sx={{ fontSize: 14, color: 'common.white !important' }} />,
                })}
              sx={{
                borderColor: isLeaf ? color : 'divider',
                color: isCurrent ? 'common.white' : chipTextColor,
                backgroundColor: isCurrent ? color : undefined,
                fontWeight: isLeaf ? 400 : 500,
                '& .MuiChip-label': { px: 1 },
                '& .MuiChip-icon': isCurrent ? { color: 'common.white !important' } : undefined,
                height: 28,
                opacity: isDimmed ? 0.4 : 1,
                ...(isLeaf &&
                  !isCurrent && {
                    '&:hover': { backgroundColor: 'action.hover' },
                  }),
              }}
            />
          </Box>
        }
      >
        {node.hasChildren && !isExpanded && <TreeItem itemId={`${node.id}-ph`} label='' sx={{ display: 'none' }} />}
        {isExpanded && node.hasChildren && (
          <PickerNodeLevel
            taxonomyId={taxonomyId}
            parentNodeId={node.id}
            depth={depth + 1}
            expandedSet={expandedSet}
            currentNodeId={currentNodeId}
            ancestorPath={ancestorPath}
            scrollContainerRef={scrollContainerRef}
            onSelect={onSelect}
            onRemove={onRemove}
            searchMatchIds={isSearchMatch ? undefined : searchMatchIds}
            searchVisibleIds={isSearchMatch ? undefined : searchVisibleIds}
          />
        )}
      </TreeItem>
      {isCurrent && onRemove && (
        <RemoveConfirmation
          anchorEl={confirmAnchor}
          onConfirm={() => {
            setConfirmAnchor(undefined);
            onRemove();
          }}
          onCancel={() => setConfirmAnchor(undefined)}
        />
      )}
    </>
  );
};

export const PickerTreeItem = memo(PickerTreeItemComponent);
